import React, { memo, useMemo } from 'react';
import { formatCurrency, formatPercent } from '@utils/formatters.util';
import useVariation from '@hooks/useVariation';
import cx from '@utils/cx.util';
import Affirm from './Affirm';
import ErrorBoundary from '@components/tailwind/ErrorBoundary';
import QuantityInput from '@components/tailwind/QuantityInput';

type Props = {
  rates: any;
  item: any;
  isCustomShop: boolean;
  minQuantity: number;
  customRate: any;
  customizationFees: any;
  addOns: any;
  quantity: number;
  setQuantity: (quantity: number) => void;
  stock: string;
  isBundle?: boolean;
};

const ProductPricing = ({
  rates,
  item,
  isCustomShop,
  minQuantity,
  customRate,
  customizationFees,
  addOns,
  quantity,
  setQuantity,
  stock,
  isBundle = false,
}: Props) => {
  const hasSalePrice = rates.salePrice !== null && rates.salePrice < rates.ourPrice;
  const isEnhancedPDPVariant = useVariation('Enhanced PDP') && !isCustomShop;

  const isGenericProduct = useMemo(() => !!item.isGenericProduct, [item]);

  const decreaseHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (stock !== 'soldOut' && quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const increaseHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (stock !== 'soldOut') {
      setQuantity(quantity + 1);
    }
  };

  if (isEnhancedPDPVariant || isBundle) {
    return (
      <div className='pricing checkout-section !tw-mb-0'>
        <div className='tw-flex tw-items-center tw-justify-between'>
          <div>
            {!isGenericProduct && stock !== 'soldOut' && !isBundle && (
              <div className='tw-inline-flex tw-min-w-[100px] tw-items-center'>
                <QuantityInput
                  isPDPVariant={true}
                  value={quantity}
                  className='tw-border-neutrals-light-grey'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    e.target.value
                      ? setQuantity(parseInt(e.target.value))
                      : setQuantity(e.target.value as any)
                  }
                  onBlur={() => !quantity && setQuantity(1)}
                  name='quantity'
                  id='quantity'
                  disabled={stock === 'soldOut'}
                  minQuantity={1}
                  decreaseHandler={decreaseHandler}
                  increaseHandler={increaseHandler}
                  large
                />
              </div>
            )}
          </div>
          <div className='tw-text-right'>
            <OurPriceVariant
              rates={rates}
              item={item}
              hasSalePrice={hasSalePrice}
              isCustomShop={isCustomShop}
              minQuantity={minQuantity}
              customRate={customRate}
              customizationFees={customizationFees}
              className='tw-justify-end'
            />
            {hasSalePrice && <Price label='Our Price' rate={rates.rate?.ourPrice} />}
            <CompetitionPrice
              savePercent={rates.youSavePercent}
              isEnhancedPDPVariant={isEnhancedPDPVariant && !isBundle}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='pricing checkout-section'>
      <div className='row g-0 align-items-center'>
        <OurPrice
          rates={rates}
          item={item}
          hasSalePrice={hasSalePrice}
          isCustomShop={isCustomShop}
          minQuantity={minQuantity}
          customRate={customRate}
          customizationFees={customizationFees}
        />
        {hasSalePrice && !isGenericProduct && (
          <Price label='Our Price' rate={rates?.ourPrice} className='tw-mt-2' />
        )}
        <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-2'>
          {rates.youSavePercent > 0 && !isGenericProduct && (
            <>
              <Price
                label={!!hasSalePrice && rates?.ourPrice ? 'Their Price' : ''}
                rate={rates?.competitorPrice}
                className='tw-mt-2'
              />
              <CompetitionPrice savePercent={rates.youSavePercent} className='tw-mt-2' />
            </>
          )}
        </div>
      </div>
      <ErrorBoundary silent>
        <Affirm
          isGenericProduct={isGenericProduct}
          isOutOfStock={item.stock === 'soldOut'}
          isCustomShop={isCustomShop}
          upcCode={item.upcCode}
          item={item}
          addOns={addOns || []}
          customizationFees={customizationFees || { total: 0, perItem: 0 }}
          customRate={customRate || null}
        />
      </ErrorBoundary>
    </div>
  );
};

export default memo(ProductPricing);

export const CompetitionPrice = ({ savePercent, className = '', isEnhancedPDPVariant = false }) => {
  if (savePercent <= 0) return null;

  return (
    <div className={cx('competitor-price', className)}>
      <div
        className={cx('tw-leading-tight', {
          'tw-text-base tw-text-[#20A458]': isEnhancedPDPVariant,
          'tw-text-sm tw-font-normal tw-text-onsale': !isEnhancedPDPVariant,
        })}>
        <span className='tw-mr-0 tw-font-bold'>{formatPercent(savePercent)} Off</span>{' '}
        <span className='tw-text-gray-dark'>vs The Competition</span>
      </div>
    </div>
  );
};

export const Price = ({ label, rate, className = '' }) => {
  if (!rate) return null;

  return (
    <div className={cx('competitor-price', className)}>
      <p className='tw-mb-0 tw-text-sm tw-font-normal tw-leading-tight !tw-text-gray-dark'>
        {label} <b className='tw-ml-1 tw-font-bold tw-line-through'>{formatCurrency(rate)}</b>
      </p>
    </div>
  );
};

export const OurPrice = ({
  rates,
  item,
  hasSalePrice,
  isCustomShop,
  minQuantity,
  customRate,
  customizationFees,
}) => {
  return (
    <div className='price col-12'>
      <meta itemProp='priceCurrency' content='USD' />
      <meta itemProp='price' content={rates.rate} />
      {item.showRateRange ? (
        <span className={`product-price sale-rate sale-rate-label ${hasSalePrice ? 'on-sale' : ''}`}>
          {hasSalePrice && <span className='prefix align-middle'>NOW </span>}
          {formatCurrency(item.rate)} - {formatCurrency(item.maxRate)}
        </span>
      ) : (
        <React.Fragment>
          <span
            className={`product-price sale-rate sale-rate-label ${hasSalePrice ? 'on-sale' : ''}`}
            data-value={rates.rate}>
            {hasSalePrice && <span className='prefix align-middle'>NOW </span>}
            {formatCurrency(rates.rate)}
          </span>
          {isCustomShop && minQuantity > 1 ? (
            <div className='bulk-unit-rate'>
              Per Unit
              <br />
              (Min. Order Qty: {minQuantity})
            </div>
          ) : (
            item.rateEach != item.rate && (
              <div className='bulk-unit-rate'>
                Per Case
                <br />(
                {formatCurrency(
                  customRate?.rateEach ?? item.rateEach + (customizationFees?.perItem ?? 0)
                )}{' '}
                each)
              </div>
            )
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export const OurPriceVariant = ({
  rates,
  item,
  hasSalePrice,
  isCustomShop,
  minQuantity,
  customRate,
  customizationFees,
  className = '',
}) => {
  const isEnhancedPDPVariant = useVariation('Enhanced PDP');

  return (
    <div className={cx('price col-12 tw-w-full', className)}>
      <meta itemProp='priceCurrency' content='USD' />
      <meta itemProp='price' content={rates.rate} />
      {item.showRateRange ? (
        <span className={`product-price sale-rate sale-rate-label ${hasSalePrice ? 'on-sale' : ''}`}>
          {hasSalePrice && <span className='prefix align-middle'>NOW </span>}
          {formatCurrency(item.rate)} - {formatCurrency(item.maxRate)}
        </span>
      ) : (
        <span className='tw-inline-flex tw-items-center tw-gap-3'>
          <span className='tw-flex tw-items-center tw-gap-3 tw-text-right'>
            {hasSalePrice && (
              <span
                className='tw-text-base tw-font-normal tw-text-core-deep-navy tw-line-through'
                data-value={rates.rate}>
                {formatCurrency(rates.ourPrice)}
              </span>
            )}
            <span
              className={cx('product-price sale-rate sale-rate-label !tw-mr-0', {
                'on-sale': hasSalePrice && !isEnhancedPDPVariant,
                '!tw-text-core-flame': isEnhancedPDPVariant,
              })}
              data-value={rates.rate}>
              {formatCurrency(rates.rate)}
            </span>
          </span>
          {isCustomShop && minQuantity > 1 ? (
            <div className='bulk-unit-rate'>
              Per Unit
              <br />
              (Min. Order Qty: {minQuantity})
            </div>
          ) : (
            item.rateEach != item.rate && (
              <div className='bulk-unit-rate'>
                Per Case
                <br />(
                {formatCurrency(
                  customRate?.rateEach ?? item.rateEach + (customizationFees?.perItem ?? 0)
                )}{' '}
                each)
              </div>
            )
          )}
        </span>
      )}
    </div>
  );
};
